import React, { useState, useEffect, useRef } from 'react';
import { PageHeader, Btn, Card, Empty } from '../../components/UI';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_COLOR = { active:'#00E74C', connecting:'#ffd700', disconnected:'#555', logged_out:'#ff6b6b' };
const STATUS_ICON  = { active:'🟢', connecting:'🟡', disconnected:'⚫', logged_out:'🔴' };

export default function WhatsApp() {
  const [numbers, setNumbers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [addLabel, setAddLabel] = useState('');
  const [showAdd, setShowAdd]   = useState(false);
  const [qrModal, setQrModal]   = useState(null);
  const [qrData, setQrData]     = useState(null);
  const [dots, setDots]         = useState('');
  const pollRef  = useRef(null);
  const dotsRef  = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/api/whatsapp/numbers'); if (data.success) setNumbers(data.numbers); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); return () => stopAll(); }, []);

  // Animated dots for "waiting" state
  useEffect(() => {
    dotsRef.current = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(dotsRef.current);
  }, []);

  const stopAll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (id) => {
    stopAll();
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/api/whatsapp/numbers/${id}/qr`);
        if (data.success) {
          setQrData(data);
          if (data.status === 'connected') {
            stopAll();
            toast.success('✅ WhatsApp connected!');
            load();
            setTimeout(() => closeModal(), 2500);
          }
          if (['logged_out','error','baileys_missing'].includes(data.status)) stopAll();
        }
      } catch(e) {}
      // Timeout after 3 minutes
      if (attempts > 90) {
        stopAll();
        setQrData({ status: 'error', message: 'Timed out. Please try again.' });
      }
    }, 2000);
  };

  const addNumber = async () => {
    if (!addLabel.trim()) { toast.error('Enter a label'); return; }
    const { data } = await api.post('/api/whatsapp/numbers', { label: addLabel.trim() });
    if (data.success) { toast.success('Number added!'); setAddLabel(''); setShowAdd(false); load(); }
    else toast.error(data.message);
  };

  const startConnect = async (num) => {
    setQrModal(num);
    setQrData({ status: 'starting' });
    try {
      const { data } = await api.post(`/api/whatsapp/numbers/${num.id}/connect`, {});
      if (data.success) {
        setQrData({ status: 'generating' });
        startPolling(num.id);
      } else {
        setQrData({ status: 'error', message: data.message || 'Failed to start' });
      }
    } catch(e) {
      setQrData({ status: 'error', message: 'Server error. Check if Node.js app is running.' });
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this WhatsApp number?')) return;
    await api.post(`/api/whatsapp/numbers/${id}/disconnect`, {});
    toast.success('Disconnected'); load();
  };

  const removeNumber = async (id) => {
    if (!window.confirm('Remove this number?')) return;
    await api.delete(`/api/whatsapp/numbers/${id}`);
    toast.success('Removed'); load();
  };

  const closeModal = () => { stopAll(); setQrModal(null); setQrData(null); };

  const retryConnect = () => { if (qrModal) startConnect(qrModal); };

  return (
    <div>
      <PageHeader title="WhatsApp Numbers" action={<Btn onClick={() => setShowAdd(!showAdd)}>➕ Add Number</Btn>} />

      <div style={{ background: 'rgba(0,231,76,0.04)', border: '1px solid rgba(0,231,76,0.12)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
        📱 <strong style={{ color: 'var(--green)' }}>Auto Rotation:</strong> Messages rotate every 10 sends · Random delay 30–60s · Daily limit 1,000/number
      </div>

      {/* Add Form */}
      {showAdd && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 14 }}>📱 Add WhatsApp Number Slot</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <input value={addLabel} onChange={e => setAddLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNumber()} placeholder="Label e.g. Sales, Support, Marketing" style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }} />
            <Btn onClick={addNumber}>Add</Btn>
            <Btn variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Numbers */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
      ) : numbers.length === 0 ? (
        <Empty icon="📱" title="No WhatsApp Numbers" desc="Add your first WhatsApp number to start sending messages." action={<Btn onClick={() => setShowAdd(true)}>➕ Add Number</Btn>} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20, marginBottom: 28 }}>
          {numbers.map(n => (
            <div key={n.id} style={{ background: 'var(--bg-card)', border: `1px solid ${n.status === 'active' ? 'rgba(0,231,76,0.3)' : 'var(--border)'}`, borderRadius: 14, padding: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{n.label}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>{n.number ? `+${n.number}` : 'Not connected yet'}</div>
                </div>
                <span style={{ background: `${STATUS_COLOR[n.status]||'#555'}18`, color: STATUS_COLOR[n.status]||'#555', padding: '4px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>
                  {STATUS_ICON[n.status]||'⚫'} {(n.status||'disconnected').toUpperCase()}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                {[['Sent Today', n.messages_sent_today||0, 'var(--green)'], ['Daily Limit', n.daily_limit||1000, 'rgba(255,255,255,0.5)']].map(([l,v,c]) => (
                  <div key={l} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 10, textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 20, color: c }}>{v}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Usage bar */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 100, height: 5, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: 'var(--green)', borderRadius: 100, width: `${Math.min(((n.messages_sent_today||0)/(n.daily_limit||1000))*100, 100)}%` }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {n.status !== 'active' ? (
                  <button onClick={() => startConnect(n)} style={{ flex: 1, background: 'var(--green)', color: '#000', border: 'none', padding: '11px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Sora' }}>
                    📱 Connect WhatsApp
                  </button>
                ) : (
                  <button onClick={() => disconnect(n.id)} style={{ flex: 1, background: 'rgba(255,200,0,0.08)', color: '#ffd700', border: '1px solid rgba(255,200,0,0.2)', padding: '11px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                    🔌 Disconnect
                  </button>
                )}
                <button onClick={() => removeNumber(n.id)} style={{ background: 'rgba(255,68,68,0.08)', color: '#ff6b6b', border: '1px solid rgba(255,68,68,0.15)', padding: '11px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 15 }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk Send */}
      <Card title="📤 Send Bulk Message">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          {[['Audience', ['All Contacts','🔴 Hot Leads','🟡 Warm Leads','✅ Customers']], ['Send Time', ['Send Now','Schedule Later']]].map(([label, opts]) => (
            <div key={label}>
              <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>{label}</label>
              <select style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13 }}>
                {opts.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>Message (Use {'{name}'} for personalization)</label>
          <textarea placeholder={`Hi {name}! Check out our latest offer...`} style={{ width: '100%', height: 100, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 12, color: '#fff', resize: 'none', fontSize: 13 }} />
        </div>
        <Btn onClick={() => numbers.some(n => n.status === 'active') ? toast.success('Queued!') : toast.error('Connect a WhatsApp number first')}>🚀 Start Sending</Btn>
      </Card>

      {/* ── QR Modal ── */}
      {qrModal && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '36px 40px', width: '100%', maxWidth: 420, textAlign: 'center' }}>

            <h2 style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 20, color: '#fff', marginBottom: 6 }}>Connect WhatsApp</h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, marginBottom: 28 }}>{qrModal.label}</p>

            {/* Starting */}
            {(qrData?.status === 'starting' || qrData?.status === 'generating') && (
              <div style={{ padding: '36px 0' }}>
                <div style={{ width: 52, height: 52, border: '3px solid rgba(255,255,255,0.08)', borderTop: '3px solid var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>
                  {qrData?.status === 'starting' ? 'Starting connection' : 'Generating QR code'}{dots}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, marginTop: 8 }}>This may take 5–15 seconds</div>
              </div>
            )}

            {/* QR Ready */}
            {qrData?.status === 'qr_ready' && qrData.qr && (
              <>
                <div style={{ background: '#fff', borderRadius: 16, padding: 14, display: 'inline-block', marginBottom: 22, boxShadow: '0 0 40px rgba(0,231,76,0.15)' }}>
                  <img src={qrData.qr} alt="QR" style={{ width: 224, height: 224, display: 'block' }} />
                </div>

                <div style={{ background: 'rgba(0,231,76,0.05)', border: '1px solid rgba(0,231,76,0.15)', borderRadius: 12, padding: '14px 18px', marginBottom: 18, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--green)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>How to scan:</div>
                  {['Open WhatsApp on your phone', 'Tap ⋮ Menu → Linked Devices', 'Tap "Link a Device"', 'Point camera at QR code above'].map((step, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
                      <span style={{ background: 'var(--green)', color: '#000', width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{i+1}</span>
                      {step}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
                  <div style={{ width: 8, height: 8, background: 'var(--green)', borderRadius: '50%', animation: 'pulse 1.5s infinite' }} />
                  Waiting for scan{dots}
                </div>
              </>
            )}

            {/* Connected */}
            {qrData?.status === 'connected' && (
              <div style={{ padding: '24px 0' }}>
                <div style={{ fontSize: 64, marginBottom: 16, animation: 'countUp 0.4s ease' }}>✅</div>
                <h3 style={{ color: 'var(--green)', marginBottom: 8, fontFamily: 'Sora', fontSize: 20 }}>Connected!</h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>WhatsApp number linked successfully.</p>
              </div>
            )}

            {/* Baileys Missing */}
            {qrData?.status === 'baileys_missing' && (
              <div style={{ padding: '16px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 14 }}>⚙️</div>
                <h3 style={{ color: '#ffd700', marginBottom: 12, fontFamily: 'Sora' }}>Setup Required</h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginBottom: 16, lineHeight: 1.7 }}>
                  WhatsApp library not found on server. Run this command in your server terminal:
                </p>
                <div style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 12, color: 'var(--green)', textAlign: 'left', marginBottom: 14, lineHeight: 1.8 }}>
                  npm install @whiskeysockets/baileys qrcode pino --ignore-scripts
                </div>
                <Btn variant="outline" onClick={() => { navigator.clipboard.writeText('npm install @whiskeysockets/baileys qrcode pino --ignore-scripts'); toast.success('Copied!'); }}>
                  📋 Copy Command
                </Btn>
              </div>
            )}

            {/* Error */}
            {qrData?.status === 'error' && (
              <div style={{ padding: '20px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 14 }}>❌</div>
                <p style={{ color: '#ff6b6b', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>{qrData.message || 'Something went wrong'}</p>
                <Btn onClick={retryConnect}>🔄 Try Again</Btn>
              </div>
            )}

            <button onClick={closeModal} style={{ marginTop: 24, background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)', padding: '9px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}